import { Loader2, WifiOff } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { VideoHubPanel } from "./VideoHubPanel";
import type { ConnectionStatus } from "@/hooks/useBridgeConnections";
import type { DeviceStateStore } from "@production-hub/shared";
import { storeToVideoHubState } from "@production-hub/shared";

interface VideoHubControlProps {
  bridgeUrl?: string;
  store?: DeviceStateStore;
  connectionId: string;
  isLoading?: boolean;
  isError?: boolean;
  connectionStatus?: ConnectionStatus;
  onUpdateValue?: (
    connectionId: string,
    path: string,
    value: string | number | boolean,
  ) => void;
}

export const VideoHubControl = ({
  store,
  connectionId,
  isLoading,
  isError,
  connectionStatus,
  onUpdateValue,
}: VideoHubControlProps) => {
  if (connectionStatus && connectionStatus !== "connected") {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12 text-muted-foreground">
          <WifiOff className="h-8 w-8 mb-2" />
          <p>Device not connected</p>
          <p className="text-sm">Connect to view and control the VideoHub</p>
        </CardContent>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12 text-muted-foreground">
          <Loader2 className="h-8 w-8 mb-2 animate-spin" />
          <p>Loading device state...</p>
        </CardContent>
      </Card>
    );
  }

  if (isError || !store) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12 text-destructive">
          <WifiOff className="h-8 w-8 mb-2" />
          <p>Failed to load device state</p>
          <p className="text-sm text-muted-foreground">
            Check that the device is responding
          </p>
        </CardContent>
      </Card>
    );
  }

  const state = storeToVideoHubState(store);

  const handleRoute = (output: number, input: number) => {
    onUpdateValue?.(connectionId, `/outputs/${output}/source`, input);
  };

  const handleLabelInput = (index: number, label: string) => {
    onUpdateValue?.(connectionId, `/inputs/${index}/label`, label);
  };

  const handleLabelOutput = (index: number, label: string) => {
    onUpdateValue?.(connectionId, `/outputs/${index}/label`, label);
  };

  return (
    <VideoHubPanel
      state={state}
      onRoute={handleRoute}
      onLabelInput={handleLabelInput}
      onLabelOutput={handleLabelOutput}
    />
  );
};
